<script setup lang="ts">
import {ExtendedProject, Role} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import {route} from "ziggy-js";
import {Head, useForm} from "@inertiajs/vue3";
import {useDate} from "@/composables/date";
import TextField from "@/components/forms/TextField.vue";
import WarningAlert from "@/components/alerts/WarningAlert.vue";

interface Props {
    project: ExtendedProject,
    role: Role,
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})
const { fromNow } = useDate()
const form = useForm({
    name: '',
    expiration_period: 30,
    scopes: [] as Array<string>,
})

const items: Array<{label: string; value: number|null}> = [
    {
        label: '30 days',
        value: 30,
    },
    {
        label: '60 days',
        value: 60,
    },
    {
        label: '90 days',
        value: 90,
    },
    {
        label: 'No expiration',
        value: null,
    }
]

const submit = () => {
    form.post(route('projects.tokens.store', {
        project: props.project.slug,
    }))
}

const has = (scope: string) => form.scopes.includes(scope)

const toggle = (scope: string) => {
    if (has(scope)) {
        form.scopes = form.scopes.filter(s => s !== scope)
    } else {
        form.scopes.push(scope)
    }
}
</script>

<template>
    <Head title="Create personal access token" />
    <project-layout
        :role="props.role"
        :project="props.project"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            New personal access token
        </h1>
        <v-divider class="mt-3" />
        <v-card
            :flat="true"
            class="mb-5"
        >
            <v-form @submit.prevent="submit">
                <v-card-text class="px-0">
                    Personal access tokens can be used to authenticate to the API over Basic Authentication.
                    <v-row class="mt-5">
                        <v-col
                            cols="12"
                            md="6"
                            class="py-0"
                        >
                            <text-field
                                v-model="form.name"
                                label="Name"
                                hint="What's this token for?"
                                :errors="form.errors.name"
                                :required="true"
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            md="8"
                            class="pb-0"
                        >
                            <v-row class="my-0">
                                <v-col
                                    cols="4"
                                    class="pb-0"
                                >
                                    <label class="black--text font-weight-bold">Expiration <span
                                        class="text-error"
                                    >*</span></label>
                                    <v-select
                                        v-model="form.expiration_period"
                                        variant="outlined"
                                        :items="items"
                                        :error-messages="form.errors.expiration_period"
                                        item-title="label"
                                        item-value="value"
                                        density="compact"
                                        bg-color="white"
                                        class="mt-2 mb-5 rounded-lg"
                                        :hide-details="true"
                                    />
                                </v-col>
                                <v-col
                                    align-self="center"
                                >
                                    <div class="mt-5">
                                        <small
                                            v-if="form.expiration_period"
                                            class="text-caption text-grey-darken-3"
                                        >
                                            The token will expire on {{ fromNow(form.expiration_period) }}
                                        </small>
                                        <small
                                            v-if="!form.expiration_period"
                                            class="text-caption text-grey-darken-3"
                                        >
                                            The token will never expire!
                                        </small>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col
                            cols="12"
                            md="10"
                            class="pt-0"
                        >
                            <warning-alert
                                v-if="!form.expiration_period"
                                class="mb-5"
                            >
                                SendCrypt strongly recommends that you set an expiration date for your token to help
                                keep your information secure.
                            </warning-alert>
                        </v-col>
                    </v-row>
                    <v-row class="mt-0">
                        <v-col
                            cols="12"
                            md="10"
                            class="py-0"
                        >
                            <label class="font-weight-bold black--text">Select scope</label>
                            <div class="text-body-2 black--text mt-2 mb-3">
                                Scopes define the access for personal tokens.
                            </div>
                            <v-card
                                variant="flat"
                                class="px-2 border"
                            >
                                <v-row :dense="true">
                                    <v-checkbox
                                        :model-value="has('uploads')"
                                        :hide-details="true"
                                        label="transfers"
                                        density="comfortable"
                                        @change="toggle('uploads')"
                                    >
                                        <template #label>
                                            <b class="text-black mr-15">transfers</b>
                                            <div class="text-body-2 ml-9">
                                                Read project transfers
                                            </div>
                                        </template>
                                    </v-checkbox>
                                </v-row>
                                <v-row :dense="true">
                                    <v-checkbox
                                        :model-value="has('users')"
                                        :hide-details="true"
                                        label="users"
                                        density="comfortable"
                                        @change="toggle('users')"
                                    >
                                        <template #label>
                                            <b class="text-black mr-15">users</b>
                                            <div class="text-body-2 ml-15">
                                                Read project users
                                            </div>
                                        </template>
                                    </v-checkbox>
                                </v-row>
                                <v-row :dense="true">
                                    <v-checkbox
                                        :model-value="has('gpg_public_keys')"
                                        :hide-details="true"
                                        label="gpg_public_keys"
                                        density="comfortable"
                                        @change="toggle('gpg_public_keys')"
                                    >
                                        <template #label>
                                            <b class="text-black mr-10">gpg_public_keys</b>
                                            <div class="text-body-2">
                                                Read project PGP public keys
                                            </div>
                                        </template>
                                    </v-checkbox>
                                </v-row>
                                <v-row :dense="true">
                                    <v-checkbox
                                        :model-value="has('ssh_public_keys')"
                                        :hide-details="true"
                                        label="ssh_public_keys"
                                        density="comfortable"
                                        @change="toggle('ssh_public_keys')"
                                    >
                                        <template #label>
                                            <b class="text-black mr-10">ssh_public_keys</b>
                                            <div class="text-body-2">
                                                Read project SSH public keys
                                            </div>
                                        </template>
                                    </v-checkbox>
                                </v-row>
                                <!--                                <v-row-->
                                <!--                                    v-if="props.project.has_groups"-->
                                <!--                                    :dense="true"-->
                                <!--                                >-->
                                <!--                                    <v-checkbox-->
                                <!--                                        :model-value="has('groups')"-->
                                <!--                                        :hide-details="true"-->
                                <!--                                        label="groups"-->
                                <!--                                        density="comfortable"-->
                                <!--                                        @change="toggle('groups')"-->
                                <!--                                    >-->
                                <!--                                        <template #label>-->
                                <!--                                            <b class="text-black mr-15">groups</b>-->
                                <!--                                            <div class="text-body-2 ml-12">-->
                                <!--                                                Read project groups-->
                                <!--                                            </div>-->
                                <!--                                        </template>-->
                                <!--                                    </v-checkbox>-->
                                <!--                                </v-row>-->
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-0 mt-3">
                    <submit-btn
                        :loading="form.processing"
                        type="submit"
                    >
                        <span class="font-weight-bold">
                            Generate token
                        </span>
                    </submit-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </project-layout>
</template>

<style scoped>

</style>
